import React, { useState, useEffect, useRef, useMemo } from "react";
import API, { EventQL } from "../../API";
import { Header, Button, Groupper, Input, Message, Icon, Modal, useFrontier, Dropdown, Table } from 'react-frontier2';
import { bindClick } from "@arema/components/Util";
import { CDN_URL } from "../../AdminConfig";
import { ShortUser, SetLoading } from "@arema/components/Classes";
import { EventSectionProps, EventData } from '../../AdminClasses';
import { Link } from "react-router-dom";
import { DeletedBanner, SearchUserModal } from "../../components";
import { useUser } from "../../AdminHooks";
import { ReactMarkdown } from 'react-markdown/lib/react-markdown';
import { useTranslation } from "react-i18next";
import MDEditor, { commands } from '@uiw/react-md-editor';
import remarkBreaks from "remark-breaks";
import UserAccess from "../../UserAccess";
import Validator from "@arema/components/Validator";

import '../../style/markdownedit.scss';

const styles = {
	placeholderPoster: {
		height: 308, 
		alignItems: 'center', 
		display: 'flex', 
		justifyContent: 'center', 
		padding: 15, 
		backgroundColor: '#f0f0f0',
		borderRadius: 8, 
		width: 308
	},
	poster: {
		width: 308,
		height: 308,
		borderRadius: 8
	}
}

enum ShownModal{
	EDIT_MODAL = 1,
	PUBLISH_MODAL = 2,
	RESPONSABLE_MODAL = 3,
	DESCRIPTION_MODAL = 4,
}

var EventContainer = (props: EventSectionProps)=>{
	var { t } = useTranslation();
	var { user, hasAccess } = useUser();
	var { Toast } = useFrontier();
	var posterInputRef = useRef<HTMLInputElement>(null);
	var [event, setEvent] = useState<EventData>(props.event);
	var [eventMeta, setEventMeta] = useState<{[x in EventQL]: any[]}>(null);
	var [status, setEventStatus] = useState<string[]>(null);
	var [loadError, setLoadError] = useState<string>(null);
	var [sinopsis, setSinopsis] = useState<string>(props.event.sinopsis);
	var [missingPoster, setMissingPoster] = useState<boolean>(false);
	var [shownModal, setShownModal] = useState<ShownModal>(null);
	var [modalPrompts, setModalPrompts] = useState<string[]>(null);
	var [posterTime, setPosterTime] = useState<number>(new Date().getTime());
	var [loadingPoster, setLoadingPoster] = useState<boolean>(false);

	var [eventName, setEventName] = useState<string>(props.event.event_name);
	var [eventType, setEventType] = useState<number>(props.event.event_type);
	var [producerName, setProducerName] = useState<string>(props.event.producer_name);
	var [promoter, setPromoter] = useState<number>(props.event.promoter_id);
	var [category, setCategory] = useState<number>(props.event.category_id);

	useEffect(()=>{
		if(!status){
			API.getEventStatus(props.event.event_id).then(res=>{
				if(res.error) return setLoadError(res.message);
				setEventStatus(res.data);
			}).catch(err=>{
				setLoadError('Hubo un error inesperado cargando la información (LCL-2)');
			})
		}
	}, []);

	var selected_promoter = useMemo(()=>{
		if(!promoter || !eventMeta?.promoter) return null;
		return eventMeta.promoter.find(a=>a.promoter_id===promoter);
	}, [promoter, eventMeta]);

	if(!status || loadError){
		return <Header loading text={loadError ? "Error" : "Cargando evento"} subtext={loadError} iconName={loadError ? 'face-frown-open' : null} />
	}

	var uploadPoster = ()=>{
		posterInputRef.current?.click();
	}

	var handlePoster = (ev: React.ChangeEvent<HTMLInputElement>)=>{
		if(!ev.target.files || ev.target.files.length==0) return;
		if(ev.target.files.length>1) return Toast.error("Favor de solo seleccionar 1 archivo.");
		var image = ev.target.files[0];
		if(image.size>(2.5*1024*1024)) return Toast.error('El poster no puede pesar mas de 2.5MB.');
		setLoadingPoster(true);
		API.setEventPoster(props.event.event_id, image).then(res=>{
			if(res.error) return Toast.error(res.message);
			setMissingPoster(false);
			setPosterTime(new Date().getTime());
			Toast.success('Se ha cambiado el poster del evento. Puede tomar un rato en propagarse el cambio.', {
				duration: 10000
			})
		}).catch(err=>{
			return Toast.error('Hubo un error subiendo el poster del evento. (LCL-3)');
		}).finally(()=>{
			setLoadingPoster(false);
		})
	}

	var editEvent = ()=>{
		setModalPrompts([]);
		setShownModal(ShownModal.EDIT_MODAL)
		setPromoter(event.promoter_id);
		setEventType(event.event_type);
		setCategory(event.category_id);
		if(!eventMeta){
			API.getEventsQL(['promoter', 'categories', 'types']).then(res=>{
				if(res.error) return Toast.error(res.message);
				setEventMeta(res.data);
			}).catch(err=>{
				setShownModal(null)
				return Toast.error('Hubo un error cargando los datos del evento. (LCL-4)');
			})
		}
	}

	var saveSinopsis = (setLoading: (s: boolean)=>void)=>{
		setLoading(true);
		API.saveSinopsis(props.event.event_id, sinopsis).then(res=>{
			if(res.error) return Toast.error(res.message);
			Toast.success('Sinopsis guardada');
		}).catch(err=>{
			return alert('Hubo un error guardando la sinopsis (LCL-5)');
		}).finally(()=>{
			setLoading(false);
		})
	}

	var saveEvent = (setLoading: (s: boolean)=>void)=>{
		var { valid, prompts, form } = Validator({
			event_name: eventName,
			promoter_id: promoter,
			category_id: category,
			event_type: eventType,
			producer_name: producerName,
		}, {
			event_name: {
				label: 'Nombre de evento',
				rules: [
					{ rule: 'minLength', params: [5], prompt: 'El nombre del evento debe de ser mínimo 5 caracteres.' },
					{ rule: 'maxLength', params: [64], prompt: 'El nombre del evento debe de ser máximo 64 caracteres.' },
					'empty'
				]
			},
			promoter_id: { label: 'Promotor', rules: ['number'] },
			category_id: { label: 'Categoría', rules: ['number'] },
			event_type: { label: 'Tipo de evento', rules: ['number'] },
			producer_name: { label: 'Productor', rules: [{ rule: 'minLength', params: [4], skipEmpty: true }] },
		});
		setModalPrompts(prompts);
		if(!valid) return;
		setLoading(true);
		API.editEvent(props.event.event_id, form.event_name, form.promoter_id, form.event_type, form.category_id, form.producer_name).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Se ha editado el evento');
			var ev = {...event};
			ev.event_name = form.event_name;
			ev.producer_name = form.producer_name;
			var category = eventMeta.categories.find(a=>a.category_id==form.category_id);
			if(category){
				ev.category_id = category.category_id;
				ev.category_name = category.category_name;
			}

			var promoter = eventMeta.promoter.find(a=>a.promoter_id==form.promoter_id);
			if(promoter){
				ev.promoter_id = promoter.promoter_id;
				ev.promoter_name = promoter.promoter_name;
			}

			var eventType = eventMeta.types.find(a=>a.type_id==form.event_type);
			if(eventType){
				ev.event_type = eventType.type_id;
				ev.type_name = eventType.description;
			}

			setEvent(ev);
			props.onEditEvent(ev);
			setShownModal(null)
		}).catch(err=>{
			setModalPrompts(['Hubo un error editando el evento (LCL-6)']);
		}).finally(()=>{
			setLoading(false);
		})
	}

	var showPublish = ()=>{
		setShownModal(ShownModal.PUBLISH_MODAL)
		setModalPrompts([]);
	}

	var publishEvent = (setLoading: SetLoading)=>{
		if(!hasAccess(UserAccess.EVENTS.PUBLISH_EVENT)) return;
		setLoading(true);
		setModalPrompts([]);
		API.setEventOptions(event.event_id, {
			published: true
		}).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			var ev = {...event};
			ev.published = true;
			setEvent(ev);
			setShownModal(null);
			Toast.success('Se ha publicado el evento!');
		}).catch(err=>{
			setModalPrompts(['Hubo un error publicando el evento. (LCL-7)']);
		}).finally(()=>{
			setLoading(false)
		});
	}

	var onResponsableChange = (admin: ShortUser)=>{
		var access = hasAccess(UserAccess.EVENTS.EDIT_USER_ASSIGNED);
		if(!access && admin.user_id!==user.user_id){
			return Toast.error('No tienes acceso a asignar otros usuarios como responsables.');
		}

		API.setEventUserAssigned(props.event.event_id, admin.user_id).then(res=>{
			if(res.error) return Toast.error(res.message);
			var new_event = {
				...event,
				user_assigned: admin.user_id,
				user_assigned_name: `${admin.first_name} ${admin.last_name}`,
			};
			props.onEditEvent(new_event);
			setEvent(new_event);
			Toast.success(`Se ha asignado a "${admin.name}" como responsable.`);
			setShownModal(null);
		}).catch(err=>{
			Toast.error('Hubo un error inesperado cambiando al responsable del evento.');
		})
	}

	return <div>
		{ !!event.deleted ? <DeletedBanner date_deleted={event.date_deleted} /> : null }
		{status && status.length>0 ? (
			<Message 
				type="error" 
				list={status} 
				header="Evento no publicado" 
				text="El evento no puede ser publicado ya que tiene información faltante."
				style={{ maxWidth: 600, margin: 'auto' }}
			/>
		) : (!status || status.length==0) && !event.published ? (
			<Message style={{ maxWidth: 400, margin: 'auto' }} centered type="info">
				<div className="header">Evento no publicado</div>
				El evento no es visible en la página publica
				{hasAccess(UserAccess.EVENTS.PUBLISH_EVENT) && (
					<Button text="Publicar" color="blue" onClick={showPublish} style={{ width: 200, display: 'block', margin: '10px auto 0 auto' }} />
				)}
			</Message>
		) : null}
		<Groupper style={{ maxWidth: 350, margin: '15px auto' }} title="Poster" actions={(
			<Button text="Subir poster" color={'black'} onClick={uploadPoster} loading={loadingPoster} />
		)}>
			{missingPoster ? (
				<div style={styles.placeholderPoster}>
					<Header text="Este evento no tiene un poster" subtext="Sube un poster para poder publicar el evento" size="small" />
				</div>
			) : (
				<img src={`${CDN_URL}/events/${props.event.event_id}/800.webp?t=${posterTime}`} style={styles.poster} onError={()=>setMissingPoster(true)} onLoad={()=>{
					setMissingPoster(false);
				}} />
			)}
			<input type="file" style={{ display: 'none' }} ref={posterInputRef} accept="image/*" onChange={handlePoster} />
		</Groupper>
		{!!event.secret && (
			<Message style={{ maxWidth: 350, margin: 'auto', marginTop: -5, marginBottom: 10, fontWeight: 'bold' }} centered>
				<Icon name="eye-slash" /> Evento secreto
			</Message>
		)}
		<Groupper fitted style={{ maxWidth: 350, margin: 'auto', marginBottom: 15 }} title="Responsable">
			{event.user_assigned ? (
				<Link to={`/users/${event.user_assigned}`} style={{ color: 'black' }}>
					<Header 
						iconName="user-circle"
						style={{ margin: 0, padding: 20 }} 
						textStyle={{ fontSize: 20, lineHeight: '24px'}} 
						subtextStyle={{ fontSize: 12, lineHeight: '12px' }} 
						text={event.user_assigned_name}
					/>
				</Link>
			) : <div style={{ margin: 15 }}>
				<Header text="Sin responsable" size="small" subtext="Este evento no tiene un usuario asignado como responsable" />
				<Button text="Asignar usuario" size="tiny" color="black" style={{ width: 150, marginTop: 15, display: 'block', margin: 'auto', padding: 8 }} onClick={()=>setShownModal(ShownModal.RESPONSABLE_MODAL)} />
			</div>}
		</Groupper>
		<Table details style={{ maxWidth: 600 }}>
			<Table.Head title="Detalles" titleRight={(
				<Button text="Editar" iconName="pencil" size="tiny" onClick={editEvent} color={'black'} />
			)} />
			<Table.Body>
				<Table.Row data={['Estatus', <>
					<Icon name={props.event.published ? 'check' : 'minus'} color={props.event.published ? 'green' : 'grey'} />
					{props.event.published ? 'Publicado' : 'No publicado'}
				</>]} />
				<Table.Row data={['Nombre', props.event.event_name]} />
				<Table.Row data={['Tipo Evento', props.event.type_name]} />
				<Table.Row data={['Promotor', props.event.promoter_name]} />
				{!!props.event.producer_name && props.event.producer_name.length>0 && (
					<Table.Row data={['Productor', props.event.producer_name]} />
				)}
				<Table.Row data={['Categoría', props.event.category_name]} />
			</Table.Body>
		</Table>
		<Groupper style={{ maxWidth: 600, margin: '15px auto' }} title="Sinopsis" fitted titleRight={(
			<Button text="Expander" iconName="expand-alt" onClick={bindClick(setShownModal, ShownModal.DESCRIPTION_MODAL)} />
		)} actions={(
			<Button text="Guardar" color='black' onClick={saveSinopsis} />
		)}>
			<div data-color-mode="light">
				<MDEditor preview="edit" value={(sinopsis || '')} onChange={setSinopsis} height={350} placeholder="Sinopsis del evento" extraCommands={[]} commands={[
					commands.bold,
					commands.italic,
					commands.strikethrough,
					commands.divider,
					commands.hr,
					commands.quote,
					commands.code,
					commands.divider,
					commands.unorderedListCommand,
					commands.orderedListCommand,
				]} />
			</div>
		</Groupper>
		<Modal open={shownModal===ShownModal.EDIT_MODAL} onClose={bindClick(setShownModal)} size={'tiny'}>
			<Modal.Header>Editar evento</Modal.Header>
			<Modal.Content>
				<Input label="Nombre del evento" value={eventName} onChange={setEventName} />
				{eventMeta ? (
					<div>
						<Dropdown search label="Promotor" value={promoter} onValueChange={setPromoter} items={eventMeta.promoter.map(a=>({
							value: a.promoter_id,
							text: a.promoter_name,
						}))} />
						<Input label="Productor" comment="Nombre del productor que se imprimirá en el boleto" placeholder={promoter ? selected_promoter.promoter_name : null} value={producerName} onChange={setProducerName} />
						<Dropdown label="Tipo Evento" value={eventType} onValueChange={setEventType} items={eventMeta.types.map(a=>({
							value: a.type_id,
							text: a.description,
						}))} />
						<Dropdown label="Categoría" value={category} onValueChange={setCategory} items={eventMeta.categories.map(a=>({
							value: a.category_id,
							text: a.category_name,
						}))} />
					</div>
				) : (
					<Header loading text="Cargando datos" />
				)}
				<Message type="error" list={modalPrompts} />
			</Modal.Content>
			<Modal.Actions>
				<Button onClick={bindClick(setShownModal)} text="Cerrar" basic />
				<Button color="black" text="Guardar" onClick={saveEvent} />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal===ShownModal.PUBLISH_MODAL} onClose={bindClick(setShownModal)} size="tiny">
			<Modal.Header>Publicar evento</Modal.Header>
			<Modal.Content>
				<Header text={`¿Publicar ${event.event_name}?`} subtext="El evento se mostrará en la página publica y se venderán boletos de los calendarios disponibles." />
			</Modal.Content>
			<Modal.Actions>
				<Button onClick={bindClick(setShownModal)} text="Cerrar" basic />
				<Button onClick={publishEvent} text="Publicar" color="black" />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal===ShownModal.DESCRIPTION_MODAL} onClose={bindClick(setShownModal)} size="large">
			<Modal.Header>Editar sinopsis</Modal.Header>
			<Modal.Content style={{ height: 'calc(100vh - 180px)', padding: 1 }}>
				<div className="ar splitscreen">
					<div className="side" data-color-mode="light">
						<MDEditor preview="edit" value={(sinopsis || '')} onChange={setSinopsis} height={'100%'} placeholder="Sinopsis del evento" extraCommands={[]} commands={[
							commands.bold,
							commands.italic,
							commands.strikethrough,
							commands.divider,
							commands.hr,
							commands.quote,
							commands.code,
							commands.divider,
							commands.unorderedListCommand,
							commands.orderedListCommand,
						]} />
					</div>
					<div className="side preview">
						<ReactMarkdown linkTarget={'_blank'} remarkPlugins={[remarkBreaks]} children={sinopsis?.replace(/\n/gi, "&nbsp; \n") || null} />
					</div>
				</div>
			</Modal.Content>
			<Modal.Actions>
				<Button text={t('common.close')} basic onClick={bindClick(setShownModal, null)} />
				<Button text={t('common.save')} color="black" onClick={saveSinopsis} />
			</Modal.Actions>
		</Modal>
		<SearchUserModal
			open={shownModal===ShownModal.RESPONSABLE_MODAL}
			onClose={bindClick(setShownModal)}
			onSelected={onResponsableChange}
		/>
	</div>
}

export default EventContainer;